import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Activity, TrendingUp, MessageSquare, User, LogOut, Crown,
  RefreshCw, ChevronRight, ArrowUpRight, ArrowDownRight 
} from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [scores, setScores] = useState({});
  const [odds, setOdds] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('mlb');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [scoresRes, oddsRes] = await Promise.all([
        axios.get(`${API}/sports/scores`),
        axios.get(`${API}/odds`)
      ]);
      
      setScores(scoresRes.data);
      setOdds(oddsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatGameTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getScoreEvents = (sport) => {
    return scores[sport]?.events || [];
  };

  const getOddsData = (sport) => {
    return odds[sport] || [];
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight">
                Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Welcome back, {user?.name?.split(' ')[0] || 'Player'}
              </p>
            </div>
            <Button 
              onClick={fetchData}
              variant="outline"
              className="border-[#1e3a5f] text-white hover:bg-[#112240]"
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <Card 
              className="bg-[#0f1e36] border-brand-lime/50 card-hover cursor-pointer"
              onClick={() => navigate('/props-picks')}
              data-testid="quick-action-props-picks"
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-brand-lime/10">
                  <Crown className="h-6 w-6 text-brand-lime" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Free Props Picks</h3>
                  <p className="text-sm text-muted-foreground">Lock of the Day + 10 daily parlays</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
              </CardContent>
            </Card>
            <Card 
              className="bg-[#0f1e36] border-[#1e3a5f] card-hover cursor-pointer"
              onClick={() => navigate('/profile')}
              data-testid="quick-action-profile"
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-orange-400/10">
                  <User className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Profile</h3>
                  <p className="text-sm text-muted-foreground">Manage your account</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground ml-auto" />
              </CardContent>
            </Card>
          </div>

          <div>
            {/* Live Scores */}
            <div>
              <Card className="bg-[#0f1e36] border-[#1e3a5f]">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                      <Activity className="h-5 w-5 text-brand-cyan" />
                      Live Scores
                    </CardTitle>
                    <span className="flex items-center gap-2 text-brand-lime text-sm">
                      <span className="w-2 h-2 bg-brand-lime rounded-full live-pulse" />
                      Live
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeSport} onValueChange={setActiveSport}>
                    <TabsList className="bg-[#0a1628] border border-[#1e3a5f] mb-4">
                      {['mlb', 'wnba', 'nfl', 'nba', 'nhl'].map(sport => (
                        <TabsTrigger 
                          key={sport} 
                          value={sport}
                          className="data-[state=active]:bg-brand-cyan data-[state=active]:text-[#0a1628] uppercase font-semibold"
                          data-testid={`sport-tab-${sport}`}
                        >
                          {sport}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {['mlb', 'wnba', 'nfl', 'nba', 'nhl'].map(sport => (
                      <TabsContent key={sport} value={sport}>
                        <ScrollArea className="h-[400px]">
                          {loading ? (
                            <div className="space-y-4">
                              {[1, 2, 3].map(i => (
                                <div key={i} className="h-20 skeleton rounded-md" />
                              ))}
                            </div>
                          ) : getScoreEvents(sport).length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                              No {sport.toUpperCase()} games scheduled today
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {getScoreEvents(sport).map((event, idx) => (
                                <div 
                                  key={idx}
                                  className="p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f] card-hover"
                                  data-testid={`game-card-${idx}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="space-y-2 flex-1">
                                      {event.competitions?.[0]?.competitors?.map((team, tidx) => (
                                        <div key={tidx} className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            {team.team?.logo && (
                                              <img 
                                                src={team.team.logo} 
                                                alt={team.team.abbreviation}
                                                className="w-6 h-6"
                                              />
                                            )}
                                            <span className={`font-semibold ${team.winner ? 'text-white' : 'text-muted-foreground'}`}>
                                              {team.team?.abbreviation || team.team?.shortDisplayName}
                                            </span>
                                          </div>
                                          <span className="font-mono text-xl font-bold text-white score-display">
                                            {team.score || '0'}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="ml-6 text-right">
                                      <div className={`text-xs uppercase tracking-wide ${
                                        event.status?.type?.state === 'in' 
                                          ? 'text-brand-lime' 
                                          : event.status?.type?.state === 'post'
                                          ? 'text-muted-foreground'
                                          : 'text-brand-cyan'
                                      }`}>
                                        {event.status?.type?.shortDetail || 'Scheduled'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Odds Comparison */}
          <Card className="bg-[#0f1e36] border-[#1e3a5f] mt-8">
            <CardHeader>
              <CardTitle className="font-heading text-xl font-bold text-white uppercase tracking-wide">
                Latest Odds - {activeSport.toUpperCase()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 skeleton rounded-md" />
                    ))}
                  </div>
                ) : getOddsData(activeSport).length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No odds available for {activeSport.toUpperCase()}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getOddsData(activeSport).slice(0, 8).map((game, idx) => (
                      <div 
                        key={idx}
                        className="p-4 bg-[#0a1628] rounded-md border border-[#1e3a5f] table-row-hover"
                        data-testid={`odds-row-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-white">
                              {game.away_team} @ {game.home_team}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {new Date(game.commence_time).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {game.bookmakers?.[0]?.markets?.[0]?.outcomes?.map((outcome, oidx) => (
                              <div key={oidx} className="text-center">
                                <div className="text-xs text-muted-foreground">
                                  {outcome.name}
                                </div>
                                <div className={`font-mono font-bold ${
                                  outcome.price > 0 ? 'odds-positive' : 'text-white'
                                }`}>
                                  {outcome.price > 0 ? '+' : ''}{outcome.price}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
